import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheckIcon, CarIcon, SmartphoneIcon, FileTextIcon, DownloadIcon, ChevronDownIcon, AlertCircleIcon } from 'lucide-react';
import { formatNaira, formatDate } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface PolicyHistoryProps {
  navigate: (screen: string) => void;
}
export function PolicyHistory({
  navigate
}: PolicyHistoryProps) {
  const [activeTab, setActiveTab] = useState('all');
  const [expandedId, setExpandedId] = useState<number | null>(1);
  const tabs = [{
    id: 'all',
    label: 'All'
  }, {
    id: 'active',
    label: 'Active'
  }, {
    id: 'pending',
    label: 'Pending'
  }];
  const policies = [{
    id: 1,
    product: 'Child Education',
    icon: ShieldCheckIcon,
    status: 'active',
    underwriter: 'Leadway Assurance Plc',
    policyNo: 'LWY/EDU/2026/001',
    premium: 16666,
    frequency: 'Monthly',
    nextPayment: '2026-04-15',
    sumAssured: 10000000,
    savingsProgress: 42,
    hasFinalCert: true
  }, {
    id: 2,
    product: 'Motor Vehicle (Comp)',
    icon: CarIcon,
    status: 'active',
    underwriter: 'AXA Mansard',
    policyNo: 'AXA/MOT/2026/842',
    premium: 150000,
    frequency: 'Annually',
    nextPayment: '2027-01-10',
    sumAssured: 5000000,
    savingsProgress: null,
    hasFinalCert: true
  }, {
    id: 3,
    product: 'Device Insurance',
    icon: SmartphoneIcon,
    status: 'pending',
    underwriter: 'Cornerstone Insurance',
    policyNo: 'Pending Registration',
    premium: 500,
    frequency: 'Monthly',
    nextPayment: '2026-04-12',
    sumAssured: 450000,
    savingsProgress: null,
    hasFinalCert: false
  }];
  const filteredPolicies = activeTab === 'all' ? policies : policies.filter(p => p.status === activeTab);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 15
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex-1 overflow-y-auto pb-24 bg-slate-50" data-id="element-633">
      <TopBar title="My Policies" onNotificationClick={() => navigate('notifications')} data-id="element-634" />

      <div className="px-5 pt-4 pb-6" data-id="element-635">
        {/* Header & Stats */}
        <div className="mb-8" data-id="element-636">
          <h1 className="text-2xl font-bold text-[#1B3B36] mb-2" data-id="element-637">Policies</h1>
          <p className="text-sm text-gray-500 mb-6" data-id="element-638">
            Manage your active policies and certificates.
          </p>

          <div className="grid grid-cols-2 gap-3" data-id="element-639">
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm" data-id="element-640">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-641">
                Active
              </p>
              <p className="text-2xl font-bold text-[#1B3B36]" data-id="element-642">3</p>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm" data-id="element-643">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-644">
                Total Value
              </p>
              <p className="text-xl font-bold text-emerald-600" data-id="element-645">₦14.5M</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto hide-scrollbar pb-1" data-id="element-646">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-5 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all ${activeTab === tab.id ? 'bg-[#1B3B36] text-white shadow-md' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'}`} data-id="element-647">
              {tab.label}
            </button>)}
        </div>

        {/* Policies List */}
        <div className="space-y-4" data-id="element-648">
          <AnimatePresence mode="popLayout" data-id="element-649">
            {filteredPolicies.map(policy => <motion.div key={policy.id} layout initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className={`bg-white rounded-3xl border overflow-hidden transition-shadow ${expandedId === policy.id ? 'border-amber-200 shadow-lg shadow-amber-500/10' : 'border-gray-100 shadow-sm hover:shadow-md'}`} data-id="element-650">
                {/* Card Header (Always visible) */}
                <div onClick={() => setExpandedId(expandedId === policy.id ? null : policy.id)} className="p-5 cursor-pointer flex items-center justify-between bg-white relative z-10" data-id="element-651">
                  <div className="flex items-center gap-4" data-id="element-652">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${policy.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`} data-id="element-653">
                      <policy.icon className="w-6 h-6" data-id="element-654" />
                    </div>
                    <div data-id="element-655">
                      <h3 className="font-bold text-[#1B3B36] text-base mb-0.5" data-id="element-656">
                        {policy.product}
                      </h3>
                      <div className="flex items-center gap-2 text-xs font-medium" data-id="element-657">
                        <span className={policy.status === 'active' ? 'text-emerald-600' : 'text-amber-600'} data-id="element-658">
                          {policy.status === 'active' ? 'Active' : 'Pending'}
                        </span>
                        <span className="text-gray-300" data-id="element-659">•</span>
                        <span className="text-gray-500" data-id="element-660">
                          {policy.underwriter}
                        </span>
                      </div>
                    </div>
                  </div>
                  <motion.div animate={{
                rotate: expandedId === policy.id ? 180 : 0
              }} transition={{
                duration: 0.2
              }} className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400" data-id="element-661">
                    <ChevronDownIcon className="w-5 h-5" data-id="element-662" />
                  </motion.div>
                </div>

                {/* Expanded Content */}
                <AnimatePresence data-id="element-663">
                  {expandedId === policy.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="border-t border-gray-50 bg-gray-50/50" data-id="element-664">
                      <div className="p-5 space-y-5" data-id="element-665">
                        {/* Key Details Grid */}
                        <div className="grid grid-cols-2 gap-4" data-id="element-666">
                          <div data-id="element-667">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-668">
                              Policy Number
                            </p>
                            <p className="text-sm font-mono font-bold text-[#1B3B36]" data-id="element-669">
                              {policy.policyNo}
                            </p>
                          </div>
                          <div data-id="element-670">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-671">
                              Sum Assured
                            </p>
                            <p className="text-sm font-bold text-[#1B3B36]" data-id="element-672">
                              {formatNaira(policy.sumAssured)}
                            </p>
                          </div>
                          <div data-id="element-673">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-674">
                              Premium
                            </p>
                            <p className="text-sm font-bold text-[#1B3B36]" data-id="element-675">
                              {formatNaira(policy.premium)}
                              <span className="text-xs text-gray-500 font-medium" data-id="element-676">
                                /{policy.frequency.toLowerCase()}
                              </span>
                            </p>
                          </div>
                          <div data-id="element-677">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-678">
                              Next Payment
                            </p>
                            <p className="text-sm font-bold text-[#1B3B36]" data-id="element-679">
                              {formatDate(policy.nextPayment)}
                            </p>
                          </div>
                        </div>

                        {/* Savings Progress (if applicable) */}
                        {policy.savingsProgress && <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm" data-id="element-680">
                            <div className="flex justify-between items-end mb-2" data-id="element-681">
                              <div data-id="element-682">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-683">
                                  Savings Goal
                                </p>
                                <p className="text-sm font-bold text-[#1B3B36]" data-id="element-684">
                                  {policy.savingsProgress}% Reached
                                </p>
                              </div>
                              <p className="text-xs font-bold text-emerald-600" data-id="element-685">
                                On track
                              </p>
                            </div>
                            <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-686">
                              <motion.div initial={{
                        width: 0
                      }} animate={{
                        width: `${policy.savingsProgress}%`
                      }} transition={{
                        duration: 1,
                        delay: 0.2
                      }} className="h-full bg-emerald-500 rounded-full" data-id="element-687" />
                            </div>
                          </div>}

                        {/* Documents Section */}
                        <div data-id="element-688">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-689">
                            Documents
                          </p>
                          <div className="space-y-2" data-id="element-690">
                            {policy.hasFinalCert ? <button className="w-full flex items-center justify-between p-3 bg-white border border-emerald-100 rounded-xl hover:border-emerald-200 transition-colors group" data-id="element-691">
                                <div className="flex items-center gap-3" data-id="element-692">
                                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600" data-id="element-693">
                                    <FileTextIcon className="w-4 h-4" data-id="element-694" />
                                  </div>
                                  <div className="text-left" data-id="element-695">
                                    <p className="text-sm font-bold text-[#1B3B36]" data-id="element-696">
                                      Final Certificate
                                    </p>
                                    <p className="text-[10px] text-gray-500" data-id="element-697">
                                      PDF • 2.4 MB
                                    </p>
                                  </div>
                                </div>
                                <DownloadIcon className="w-5 h-5 text-gray-400 group-hover:text-emerald-600 transition-colors" data-id="element-698" />
                              </button> : <div className="w-full flex items-center justify-between p-3 bg-white border border-amber-100 rounded-xl" data-id="element-699">
                                <div className="flex items-center gap-3" data-id="element-700">
                                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600" data-id="element-701">
                                    <AlertCircleIcon className="w-4 h-4" data-id="element-702" />
                                  </div>
                                  <div className="text-left" data-id="element-703">
                                    <p className="text-sm font-bold text-[#1B3B36]" data-id="element-704">
                                      Temporary Cover Note
                                    </p>
                                    <p className="text-[10px] text-amber-600 font-medium" data-id="element-705">
                                      Final cert pending underwriter approval
                                    </p>
                                  </div>
                                </div>
                                <button className="p-2 text-gray-400 hover:text-amber-600 transition-colors" data-id="element-706">
                                  <DownloadIcon className="w-5 h-5" data-id="element-707" />
                                </button>
                              </div>}

                            <button className="w-full flex items-center justify-between p-3 bg-white border border-gray-100 rounded-xl hover:border-gray-200 transition-colors group" data-id="element-708">
                              <div className="flex items-center gap-3" data-id="element-709">
                                <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-500" data-id="element-710">
                                  <FileTextIcon className="w-4 h-4" data-id="element-711" />
                                </div>
                                <div className="text-left" data-id="element-712">
                                  <p className="text-sm font-bold text-[#1B3B36]" data-id="element-713">
                                    Policy Document
                                  </p>
                                  <p className="text-[10px] text-gray-500" data-id="element-714">
                                    Terms & Conditions
                                  </p>
                                </div>
                              </div>
                              <DownloadIcon className="w-5 h-5 text-gray-400 group-hover:text-[#1B3B36] transition-colors" data-id="element-715" />
                            </button>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 pt-2" data-id="element-716">
                          <button className="flex-1 py-3 bg-white border border-gray-200 rounded-xl text-sm font-bold text-[#1B3B36] shadow-sm hover:bg-gray-50 transition-colors" data-id="element-717">
                            Make a Claim
                          </button>
                          <button className="flex-1 py-3 bg-[#1B3B36] rounded-xl text-sm font-bold text-white shadow-md hover:bg-[#0F2B27] transition-colors" data-id="element-718">
                            Pay Premium
                          </button>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>;
}